import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const VERDICT_STYLES = {
  series_aligned_field_bug: {
    border: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950',
    Icon: AlertTriangle,
    iconColor: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  },
  series_aligned_padding_bug: {
    border: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950',
    Icon: AlertTriangle,
    iconColor: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  },
  series_disjoint_renumbered: {
    border: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950',
    Icon: XCircle,
    iconColor: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  xero_set_incomplete: {
    border: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950',
    Icon: XCircle,
    iconColor: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
};

function VerdictBanner({ verdict }) {
  if (!verdict) return null;
  const style = VERDICT_STYLES[verdict.code] || VERDICT_STYLES.xero_set_incomplete;
  const { Icon, iconColor, border, badge } = style;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${border}`}>
      <Icon className={`h-5 w-5 ${iconColor} mt-0.5 shrink-0`} />
      <div className="space-y-1">
        <Badge className={`text-xs ${badge}`}>{verdict.code}</Badge>
        <p className="text-sm">{verdict.detail}</p>
      </div>
    </div>
  );
}

function SeriesBox({ title, data }) {
  if (!data) return null;
  return (
    <div className="border rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <span className="text-muted-foreground">Count</span>
        <span className="tabular-nums">{fmtInt(data.count)}</span>
        <span className="text-muted-foreground">Distinct normalized</span>
        <span className="tabular-nums">{fmtInt(data.normalized_distinct)}</span>
        <span className="text-muted-foreground">Numeric range</span>
        <span className="tabular-nums">
          {data.min_number != null ? `${data.min_number} - ${data.max_number}` : '-'}
        </span>
      </div>
      {data.primary_field && (
        <p className="text-[10px] text-muted-foreground">
          Primary field: <code className="font-mono">{data.primary_field}</code>
        </p>
      )}
    </div>
  );
}

function RawSampleTable({ title, samples }) {
  if (!samples || samples.length === 0) return null;
  return (
    <details>
      <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
        {title} ({samples.length})
      </summary>
      <div className="mt-2 overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-2 py-1.5 font-medium">#</th>
              <th className="text-left px-2 py-1.5 font-medium">Raw value</th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-2 py-1 text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-1 font-mono">{s || '(empty)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export function InvoiceSeriesAlignmentPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoiceSeriesAlignment();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoice Series Alignment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          DB-only check (zero Xero API calls). Compares normalized invoice numbers
          between source:bros and source:xero to determine whether the series overlap.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Verdict banner */}
            <VerdictBanner verdict={result.verdict} />

            {/* Numeric overlap (padding-insensitive) */}
            {result.numeric_overlap && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Numeric Overlap (padding-insensitive)</p>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <span className="text-muted-foreground" />
                  <span className="font-medium text-center">Bros</span>
                  <span className="font-medium text-center">Xero</span>
                  <span className="text-muted-foreground">Count</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.bros_integers?.count)}</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.xero_integers?.count)}</span>
                  <span className="text-muted-foreground">Min</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.bros_integers?.min)}</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.xero_integers?.min)}</span>
                  <span className="text-muted-foreground">Max</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.bros_integers?.max)}</span>
                  <span className="tabular-nums text-center">{fmtInt(result.numeric_overlap.xero_integers?.max)}</span>
                </div>
                {(result.numeric_overlap.xero_integers?.garbage_excluded > 0 ||
                  result.numeric_overlap.xero_integers?.empty_excluded > 0) && (
                  <p className="text-[10px] text-muted-foreground">
                    Xero excluded: {fmtInt(result.numeric_overlap.xero_integers.garbage_excluded)} garbage,{' '}
                    {fmtInt(result.numeric_overlap.xero_integers.empty_excluded)} empty
                  </p>
                )}
                <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                  <span className="text-muted-foreground font-semibold">Numeric match count</span>
                  <span className="tabular-nums font-semibold">{fmtInt(result.numeric_overlap.numeric_match_count)}</span>
                </div>
                {result.numeric_overlap.matched_integers_sample?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-[10px] text-muted-foreground mb-1">Matched integers:</p>
                    <div className="flex flex-wrap gap-1">
                      {result.numeric_overlap.matched_integers_sample.map((n) => (
                        <Badge key={n} variant="outline" className="text-[10px] font-mono tabular-nums">{n}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tenant resolution */}
            {result.tenant_resolution && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Tenant Resolution</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">Synced tenants (source:xero)</span>
                  <span className="tabular-nums">{fmtInt(result.tenant_resolution.synced_tenant_count)}</span>
                  <span className="text-muted-foreground">BRL in synced set?</span>
                  <span>
                    {result.tenant_resolution.brl_in_synced_set
                      ? <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Yes</Badge>
                      : <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">No</Badge>
                    }
                  </span>
                  <span className="text-muted-foreground">BRL connected (OAuth)?</span>
                  <span>
                    {result.tenant_resolution.brl_connected
                      ? <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Yes</Badge>
                      : <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">No</Badge>
                    }
                  </span>
                </div>
                {result.tenant_resolution.synced_tenants?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-muted-foreground mb-1">Synced tenants:</p>
                    <div className="overflow-x-auto border rounded">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1 font-medium">Tenant GUID</th>
                            <th className="text-left px-2 py-1 font-medium">Resolved Name</th>
                            <th className="text-right px-2 py-1 font-medium">Invoices</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.tenant_resolution.synced_tenants.map((t) => (
                            <tr key={t.xero_tenant_id} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono text-[10px]">{t.xero_tenant_id}</td>
                              <td className="px-2 py-1">{t.resolved_name}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmtInt(t.invoice_count)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {result.tenant_resolution.connected_tenant_names?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-muted-foreground mb-1">Connected tenants (OAuth layer):</p>
                    <div className="flex flex-wrap gap-1">
                      {result.tenant_resolution.connected_tenant_names.map((n) => (
                        <Badge key={n} variant="outline" className="text-[10px]">{n}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* String overlap (original, kept for comparison) */}
            {result.overlap && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  String Overlap (original, {fmtInt(result.overlap.match_count)} matches)
                </summary>
                <div className="mt-2 border rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-muted-foreground">Bros normalized</span>
                    <span className="tabular-nums">{fmtInt(result.overlap.bros_normalized_count)}</span>
                    <span className="text-muted-foreground">Xero normalized</span>
                    <span className="tabular-nums">{fmtInt(result.overlap.xero_normalized_count)}</span>
                    <span className="text-muted-foreground font-semibold">Match count</span>
                    <span className="tabular-nums font-semibold">{fmtInt(result.overlap.match_count)}</span>
                  </div>
                  {result.overlap.matched_sample?.length > 0 && (
                    <div className="mt-1">
                      <p className="text-[10px] text-muted-foreground mb-1">Matched numbers:</p>
                      <div className="flex flex-wrap gap-1">
                        {result.overlap.matched_sample.map((n) => (
                          <Badge key={n} variant="outline" className="text-[10px] font-mono">{n}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Series boxes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SeriesBox title="Bros Series" data={result.bros_series} />
              <SeriesBox title="Xero Series" data={result.xero_series} />
            </div>

            {/* Field check */}
            {result.field_check && (
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Field Check</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">Twin-join queries</span>
                  <span className="font-mono">{result.field_check.twin_join_queries}</span>
                  <span className="text-muted-foreground">Populated count</span>
                  <span className="tabular-nums">{fmtInt(result.field_check.twin_join_field_populated_count)}</span>
                  <span className="text-muted-foreground">Field mismatch?</span>
                  <span>
                    {result.field_check.field_mismatch
                      ? <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">YES</Badge>
                      : <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">No</Badge>
                    }
                  </span>
                </div>
                {result.field_check.actual_field && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Data lives in: <code className="font-mono">{result.field_check.actual_field}</code>
                  </p>
                )}
                {/* Field presence */}
                {result.xero_series?.field_presence && (
                  <details className="mt-1">
                    <summary className="text-[10px] text-muted-foreground cursor-pointer">
                      Field presence on source:xero docs
                    </summary>
                    <div className="grid grid-cols-2 gap-1 text-[10px] mt-1">
                      {Object.entries(result.xero_series.field_presence).map(([f, c]) => (
                        <span key={f}>
                          <code className="font-mono">{f}</code>: {fmtInt(c)}
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Spot probe */}
            {result.spot_probe && result.spot_probe.length > 0 && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Spot Probe</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Probe</th>
                        <th className="text-left px-2 py-1.5 font-medium">Normalized</th>
                        <th className="text-center px-2 py-1.5 font-medium">Found?</th>
                        <th className="text-left px-2 py-1.5 font-medium">Matches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.spot_probe.map((sp) => (
                        <tr key={sp.probe} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{sp.probe}</td>
                          <td className="px-2 py-1 font-mono">{sp.normalized}</td>
                          <td className="px-2 py-1 text-center">
                            {sp.found
                              ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 inline" />
                              : <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 inline" />
                            }
                          </td>
                          <td className="px-2 py-1 text-[10px]">
                            {sp.matches?.length > 0
                              ? sp.matches.map((m, i) => (
                                  <span key={i} className="mr-2">
                                    <code className="font-mono">{m.field}</code>: {m.raw_value}
                                  </span>
                                ))
                              : <span className="text-muted-foreground">none</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Raw sample tables */}
            <RawSampleTable title="Bros raw invoice numbers" samples={result.bros_series?.sample_raw} />
            <RawSampleTable title="Xero raw invoice numbers" samples={result.xero_series?.sample_raw} />

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
